import {
  MadlibDataType,
  FileUploadQuestionInputValue,
  Question,
  VisibilityCondition,
  VisibilityEvaluationContext
} from '../types/madlib';

export const isFileUploadQuestionInputValue = (
  val: any
): val is FileUploadQuestionInputValue => {
  if (typeof val === 'object' && val !== null) {
    const keys = Object.keys(val);

    return !!(
      keys.includes('sanitizedFileName') &&
      keys.includes('storagePath') &&
      keys.includes('linkToUploadedFile')
    );
  }

  return false;
};

export const clearDataForEditorOverride = (
  data: MadlibDataType | null
): MadlibDataType => {
  // Keep only the file uploads, destroy all the other question answers
  const questionTemplateData: MadlibDataType['questionTemplateData'] = {};
  for (const e of Object.entries(data?.questionTemplateData ?? {})) {
    const [k, v] = e;
    if (isFileUploadQuestionInputValue(v)) {
      questionTemplateData[k] = v;
    }
  }

  return {
    templateData: {},
    questionTemplateData,
    metadata: {},
    editorOverride: true
  };
};

/**
 * Evaluates whether a question should be visible based on its visibility conditions
 */
export const evaluateVisibility = (
  question: Question,
  context: VisibilityEvaluationContext
): boolean => {
  if (question.visibilityCondition) {
    return evaluateCondition(question.visibilityCondition, context);
  }

  if (question.visibilityRule) {
    const results = question.visibilityRule.conditions.map(condition =>
      evaluateCondition(condition, context)
    );

    return question.visibilityRule.logic === 'and'
      ? results.every(Boolean)
      : results.some(Boolean);
  }

  return true; // Visible by default
};

/**
 * Evaluates a single visibility condition
 */
const evaluateCondition = (
  condition: VisibilityCondition,
  context: VisibilityEvaluationContext
): boolean => {
  const sourceData = context[condition.source];
  const fieldValue = getNestedValue(sourceData, condition.field);

  switch (condition.operator) {
    case 'equals':
      return fieldValue === condition.value;
    case 'not_equals':
      return fieldValue !== condition.value;
    case 'exists':
      return fieldValue !== undefined && fieldValue !== null;
    case 'not_exists':
      return fieldValue === undefined || fieldValue === null;
    case 'contains':
      return (
        typeof fieldValue === 'string' &&
        typeof condition.value === 'string' &&
        fieldValue.includes(condition.value)
      );
    case 'greater_than':
      return (
        typeof fieldValue === 'number' &&
        typeof condition.value === 'number' &&
        fieldValue > condition.value
      );
    case 'less_than':
      return (
        typeof fieldValue === 'number' &&
        typeof condition.value === 'number' &&
        fieldValue < condition.value
      );
    default:
      return true;
  }
};

/**
 * Helper to get nested values using dot notation (e.g., "layout.hideContent")
 */
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj);
};

/**
 * Filters an array of questions to only include visible ones
 */
export const getVisibleQuestions = (
  questions: Question[],
  context: VisibilityEvaluationContext
): Question[] => {
  return questions.filter(question => evaluateVisibility(question, context));
};
